import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, SectionTag } from "@/components/Reveal";

const sections = [
    {
        n: "01",
        title: "Agreement & Who We Are",
        body: "These Terms of Service are between you and Brickhouse One LLC, an Ohio limited liability company operating as Camarca Digital Designs (\"Camarca\", \"I\", \"me\"), based in Cincinnati, Ohio. By requesting a quote, paying a deposit, purchasing a service, or using a website built by Camarca, you agree to these terms.",
    },
    {
        n: "02",
        title: "Services",
        body: "Camarca provides custom website design and development, logo design, branding kits, website updates, and ongoing hosting and maintenance. The exact scope of every project is agreed in writing (quote, email, or message) before work begins. Anything outside the agreed scope is quoted separately.",
    },
    {
        n: "03",
        title: "Quotes & Pricing",
        body: "Prices shown on this website are starting points. Final pricing is confirmed in writing before any work or payment. E-commerce websites and projects with heavier integration are typically around $600. You will always know the full price before committing — no surprise charges, ever.",
    },
    {
        n: "04",
        title: "Payment Terms",
        body: "Every project starts with a 40% deposit, with the remaining balance due when the website is complete. The deposit reserves your build slot — once design work has begun, deposits are non-refundable. Payments are accepted by card (Stripe) or Venmo. Work may be paused if agreed payments are not made.",
    },
    {
        n: "05",
        title: "Your Responsibilities",
        body: "You agree to provide accurate business information, content and images you have the rights to use, and timely feedback during the build. Delays in feedback delay your launch date. You are responsible for the accuracy and legality of the business content on your website (pricing, claims, licensing, and similar).",
    },
    {
        n: "06",
        title: "Revisions & Approval",
        body: "Revisions are included during the build until you approve the final result — the goal is a site that fits your business. Your approval (or the site going live at your request) counts as final acceptance. After launch, minor updates are covered by the Hosting & Maintenance plan; larger changes or new pages are quoted separately.",
    },
    {
        n: "07",
        title: "Hosting & Maintenance",
        body: "Websites built by Camarca are hosted and maintained for $25 per month, billed automatically. This keeps your website live, secure, and operational, and includes minor updates (text, prices, photos) and support. If a payment fails or the subscription is cancelled, the website may be taken offline 14 days after a missed payment and restored once payments resume. You may cancel anytime — service runs to the end of the paid month.",
    },
    {
        n: "08",
        title: "Ownership & Intellectual Property",
        body: "You always own your domain name and your business content. Full ownership of the finished website transfers to you when final payment is received. Camarca may display completed work in its portfolio and marketing unless you ask otherwise in writing. You confirm you own or have rights to any materials (logos, photos, text) you provide.",
    },
    {
        n: "09",
        title: "Third-Party Services",
        body: "Your website may use third-party services — payment processors, booking systems, analytics, fonts, maps, and similar tools — which are governed by their own terms and may change, break, or be discontinued by their providers. Camarca is not responsible for third-party outages or changes, but will help find alternatives if something breaks.",
    },
    {
        n: "10",
        title: "Cancellation & Refunds",
        body: "Either side may cancel a project at any time. If a project is cancelled, completed work is billed and any unused portion of payments beyond the deposit is returned. Deposits are non-refundable once work has begun. Hosting & Maintenance can be cancelled anytime and stops at the end of the paid month; partial months are not refunded.",
    },
    {
        n: "11",
        title: "Limitation of Liability",
        body: "Every site is built with care and tested before launch, but websites are provided as-is after approval. Camarca is not liable for lost revenue, downtime, third-party failures, or other indirect losses. Maximum liability for any claim is limited to the amount you paid in the three months before the claim.",
    },
    {
        n: "12",
        title: "Governing Law & Changes",
        body: "These terms are governed by the laws of the State of Ohio. Terms may be updated occasionally — the current version always lives on this page, and continuing to use services after an update means you accept the new version. Questions? Email CamarcaDigitalDesigns@gmail.com.",
    },
];

export default function Terms() {
    return (
        <div data-testid="terms-page">
            <SEO
                title="Terms of Service | Camarca Digital Designs — Brickhouse One LLC"
                description="Terms of service for Camarca Digital Designs (Brickhouse One LLC): 40% deposit payment terms, refunds, $25/month hosting and maintenance, ownership, revisions, cancellation and liability policies for website design clients."
                path="/terms"
            />

            <header className="mx-auto max-w-3xl px-6 pb-14 pt-40 lg:px-0">
                <Reveal>
                    <SectionTag>Terms of Service</SectionTag>
                    <h1 className="mt-6 font-display text-4xl font-black leading-tight tracking-tight md:text-6xl">
                        Terms,{" "}
                        <span className="bg-gradient-to-r from-neon to-deep bg-clip-text text-transparent">in plain English.</span>
                    </h1>
                    <p className="mt-8 leading-relaxed text-zinc-400 md:text-lg">
                        No legal maze. This is what working together looks like — what you get, what things cost, and who owns what.
                    </p>
                    <p className="mt-4 font-mono text-xs leading-relaxed tracking-wider text-zinc-500">
                        Camarca Digital Designs is a trade name of Brickhouse One LLC, an Ohio limited liability company.
                    </p>
                </Reveal>
            </header>

            <div className="mx-auto max-w-3xl px-6 pb-24 lg:px-0">
                {sections.map((s, i) => (
                    <Reveal key={s.n} delay={i * 0.05}>
                        <article data-testid={`terms-section-${s.n}`} className="border-t border-white/10 py-10">
                            <div className="flex items-baseline gap-6">
                                <span className="font-mono text-sm text-neon">{s.n}</span>
                                <div>
                                    <h2 className="font-display text-xl font-bold md:text-2xl">{s.title}</h2>
                                    <p className="mt-4 leading-relaxed text-zinc-400">{s.body}</p>
                                </div>
                            </div>
                        </article>
                    </Reveal>
                ))}

                <Reveal className="mt-8">
                    <div className="flex flex-wrap items-center justify-between gap-6 border border-white/10 bg-surface p-8">
                        <div>
                            <h2 className="font-display text-xl font-bold">Ready when you are.</h2>
                            <p className="mt-2 text-sm text-zinc-400">40% starts your build. The rest when you love it.</p>
                        </div>
                        <Link
                            to="/contact"
                            data-testid="terms-cta"
                            className="group flex items-center gap-3 bg-neon px-6 py-3.5 font-mono text-xs font-medium tracking-[0.2em] uppercase text-void transition-shadow duration-300 hover:shadow-[0_0_25px_rgba(0,240,255,0.5)]"
                        >
                            Start a Project
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </div>
                    <p className="mt-8 font-mono text-xs text-zinc-600">Last updated: August 2026 · Camarca Digital Designs, Cincinnati, Ohio</p>
                </Reveal>
            </div>
        </div>
    );
}
